"use client";

import { BlurFade } from "@/components/ui/blur-fade";
import { X, Check } from "lucide-react";

interface ComparisonRow {
  feature: string;
  free: boolean;
  tiera: boolean;
}

interface ComparisonSectionProps {
  title: string;
  description: string;
  columnA?: string;
  columnB?: string;
  data: ComparisonRow[];
}

const ComparisonSection = ({
  title,
  description,
  columnA = "Free VPNs",
  columnB = "Tiera VPN",
  data,
}: ComparisonSectionProps) => {
  if (!data || data.length === 0) return null;

  return (
    <section className="py-12 md:py-24 px-4 md:px-6 bg-white">
      <div className="container max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center space-y-3 md:space-y-6 max-w-3xl mx-auto mb-8 md:mb-12">
          <BlurFade delay={0.1}>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              {title}
            </h2>
          </BlurFade>
          <BlurFade delay={0.2}>
            <p className="text-sm md:text-base text-foreground/60 leading-relaxed font-medium">
              {description}
            </p>
          </BlurFade>
        </div>

        {/* Comparison Table */}
        <BlurFade delay={0.3}>
          <div className="rounded-2xl md:rounded-3xl border border-foreground/10 overflow-hidden bg-muted/20">
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_80px_80px] md:grid-cols-[1fr_140px_140px] bg-muted/50 border-b border-foreground/10">
              <div className="py-3 md:py-4 px-4 md:px-6 text-xs md:text-sm font-bold text-foreground uppercase tracking-wider">
                Feature
              </div>
              <div className="py-3 md:py-4 px-2 md:px-4 text-xs md:text-sm font-bold text-foreground uppercase tracking-wider text-center">
                {columnA}
              </div>
              <div className="py-3 md:py-4 px-2 md:px-4 text-xs md:text-sm font-bold text-primary uppercase tracking-wider text-center">
                {columnB}
              </div>
            </div>

            {/* Rows */}
            {data.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-[1fr_80px_80px] md:grid-cols-[1fr_140px_140px] border-b border-foreground/5 last:border-b-0 ${
                  i % 2 === 0 ? "bg-white" : "bg-muted/20"
                } hover:bg-muted/40 transition-colors`}
              >
                <div className="py-3 md:py-4 px-4 md:px-6 text-xs md:text-sm font-medium text-foreground">
                  {row.feature}
                </div>
                <div className="py-3 md:py-4 px-2 md:px-4 flex items-center justify-center">
                  {row.free ? (
                    <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-green-100 flex items-center justify-center">
                      <Check size={14} className="text-green-600 md:w-4 md:h-4" strokeWidth={2.5} />
                    </div>
                  ) : (
                    <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-red-50 flex items-center justify-center">
                      <X size={14} className="text-red-400 md:w-4 md:h-4" strokeWidth={2.5} />
                    </div>
                  )}
                </div>
                <div className="py-3 md:py-4 px-2 md:px-4 flex items-center justify-center">
                  {row.tiera ? (
                    <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check size={14} className="text-primary md:w-4 md:h-4" strokeWidth={2.5} />
                    </div>
                  ) : (
                    <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-red-50 flex items-center justify-center">
                      <X size={14} className="text-red-400 md:w-4 md:h-4" strokeWidth={2.5} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default ComparisonSection;





